import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-checkbox-blank-circle-line',
  'Jackets': 'ri-price-tag-3-line',
  'Pants/Shorts': 'ri-scissors-line',
  'Headwear': 'ri-sun-line',
  'Sets': 'ri-archive-line',
  'Underwear/Underpants': 'ri-shirt-line',
  'Jersey': 'ri-medal-line',
  'Accessories': 'ri-star-line',
  'Bags': 'ri-shopping-bag-line',
  'Belts': 'ri-donut-chart-line',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDesktopDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDesktopDropdownOpen(false);
    }, 200);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg transition-transform duration-300 group-hover:scale-105">
                <i className="ri-stack-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950 whitespace-nowrap">
                Superbuy Spread
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Home
              </Link>

              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                      desktopDropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                {desktopDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 overflow-hidden">
                    <div className="max-h-[420px] overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/superbuy-spreadsheet-guide"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Guide
              </Link>

              <Link
                to="/superbuy-spreadsheet-safety-guide"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Safety
              </Link>

              <Link
                to="/superbuy-spreadsheet-vs-traditional"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Compare
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>

              <button
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                  if (mobileMenuOpen) setMobileCategoryOpen(false);
                }}
                aria-label="Toggle menu"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-950 text-xl transition-transform duration-200`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          <div className="absolute top-0 right-0 w-[85vw] max-w-[380px] h-full bg-background-50 shadow-2xl overflow-y-auto animate-slide-in-right">
            <div className="sticky top-0 z-10 bg-background-50/95 backdrop-blur-md flex items-center justify-between px-6 h-16 border-b border-background-200/50">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-md">
                  <i className="ri-stack-line text-background-50 text-sm"></i>
                </div>
                <span className="font-heading text-base font-semibold text-foreground-950">Menu</span>
              </Link>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-close-line text-foreground-700 text-lg"></i>
              </button>
            </div>

            <div className="px-5 py-5 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-foreground-600 text-lg"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800">Home</span>
              </Link>

              <div className="pt-2">
                <button
                  className="flex items-center justify-between w-full px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                      <i className="ri-grid-line text-foreground-600 text-lg"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">Categories</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                      {categories.length}
                    </span>
                    <i className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    mobileCategoryOpen ? 'max-h-[600px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="grid grid-cols-1 gap-0.5 px-2 pb-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center group-hover:bg-accent-100/60 transition-colors">
                          <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-foreground-500 group-hover:text-accent-600 text-sm transition-colors`}></i>
                        </div>
                        <span className="text-sm text-foreground-700 group-hover:text-foreground-950 transition-colors flex-1">
                          {cat.name}
                        </span>
                        <i className="ri-arrow-right-up-line text-foreground-300 text-xs group-hover:text-accent-500 transition-colors"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/superbuy-spreadsheet-guide"
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-foreground-600 text-lg"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800">Complete Guide</span>
              </Link>

              <Link
                to="/superbuy-spreadsheet-safety-guide"
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-foreground-600 text-lg"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800">Safety Guide</span>
              </Link>

              <Link
                to="/superbuy-spreadsheet-vs-traditional"
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-scales-3-line text-foreground-600 text-lg"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800">Compare</span>
              </Link>
            </div>

            <div className="mx-5 my-5 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
              <p className="text-xs text-foreground-500 mb-3">Ready to shop?</p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy it Now
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full mt-2 px-4 py-3 bg-foreground-950 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
                <i className="ri-external-link-line text-base"></i>
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </>
  );
}